import { documentStore, uiStore } from "@/store/store";
import { pipe } from "fp-ts/function";
import { fold, isRight } from "fp-ts/Either";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import * as t from "io-ts";
import { saveToWordPressWithErrorsLoggedToUiStore } from "@/lib/wordpress/save";
import { deleteNode, findNode } from "@/lib/tree/tree";
import { BarebonesSectionLibrary } from "./barebonesLibrary";
import { SECTION_SLUG } from "@/lib/elements/constants";
import { ExportedTreeV, TreeNode } from "@/types/tree";
import { PageDescription, PageAISettings } from "../types";
import { jsonStringifyOrUndefined } from "@/util/json-stringify-or-undefined";
import { getBarebonesSectionLibrary } from "./barebonesLibrary";
import { pasteElements } from "@/lib/design-library/design-library";
import { SectionDescription } from "../types";
import { PromptInfo } from "@/lib/ai/types";
import { breakdanceUndo } from "@/lib/undo/undo";
import defensiveCopy from "@/util/defensive-copy";
import lodash from "lodash";
import { DocumentMeta } from "@/types/document";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import { ExpectedDataToLoadDocumentV } from "@/types/load";
import { putDataInStoreFromDocumentLoadedFromWordpress } from "@/lib/wordpress/load";
import { getPromptInfoFromNode } from "./ai";
import { fetchAiSettings } from "@/lib/ai/api";

export const loadDocumentFromWordPressToOnboardingPage = async (
  postId: number | undefined
) => {
  return pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_load_document",
      dataToPost: { id: postId },
      decoder: ExpectedDataToLoadDocumentV
    }),
    fold(
      () => null,
      loadedData => {
        if ("errorType" in loadedData) {
          return;
        }

        putDataInStoreFromDocumentLoadedFromWordpress(loadedData);
        return loadedData;
      }
    )
  );
};

export function traverseCanvasDocumentAndToggleSectionVisibilityWithCss(
  nodeIds: number[]
) {
  if (!window.Breakdance.canvas || !window.Breakdance.canvas.document) {
    return;
  }
  const canvasDocument = window.Breakdance.canvas.document;

  canvasDocument.querySelectorAll(".bde-section").forEach(element => {
    (element as HTMLElement).style.display = "block";
  });

  if (!nodeIds || nodeIds.length === 0) {
    return;
  }

  // show the specified sections
  nodeIds.forEach((nodeId: number) => {
    const element = canvasDocument.querySelector(`[data-node-id="${nodeId}"]`);
    if (element) {
      (element as HTMLElement).style.display = "none";
    }
  });
}

export async function trashOnboardingPagesWordpress(pageIdsToTrash: number[]) {
  return pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_onboarding_trash_pages",
      decoder: t.type({ success: t.string }),
      friendlyName: `Trash onboarding pages"`,
      dataToPost: { pageIdsToTrash }
    }),
    fold(
      () => null,
      response => response
    )
  );
}

export async function removeHomepageSectionsWithBreakdance(
  homepageId: number,
  sectionsIdsToRemove: number[]
) {
  if (sectionsIdsToRemove.length === 0) {
    return;
  }

  await loadDocumentFromWordPressToOnboardingPage(homepageId);

  sectionsIdsToRemove.forEach(sectionId => {
    deleteNode(documentStore.document.tree, sectionId);
  });

  return await saveToWordPressWithErrorsLoggedToUiStore();
}

export async function getDesignSetBarebonesData(): Promise<DesignSetBarebonesData | void> {
  const url = "https://breakdancelibrary.com/barebones";

  return pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_get_design_set_barebones_data_from_remote",
      decoder: DesignSetBarebonesDataV,
      friendlyName: "Design Set Barebones Data",
      dataToPost: { url }
    }),
    fold(
      () => {
        return;
      },
      (data: DesignSetBarebonesData) => {
        return data;
      }
    )
  );
}

export const DesignSetBarebonesDataV = t.type({
  posts: t.array(
    t.type({
      title: t.string,
      tree: t.unknown
    })
  )
});

export type DesignSetBarebonesData = t.TypeOf<typeof DesignSetBarebonesDataV>;

export async function getPostsWithValidTreeFromRemote() {
  const designSetBarebonesData = await getDesignSetBarebonesData();

  if (!designSetBarebonesData) return false;

  const aiSettings = await fetchAiSettings();
  if (!aiSettings) return false;

  const postsWithValidTree = designSetBarebonesData.posts.filter(
    (post: { title: string; tree: unknown }) => {
      return !!post.tree && isRight(ExportedTreeV.decode(post.tree));
    }
  );

  return postsWithValidTree;
}

export function convertPostsWithValidTreeToBarebonesSectionLibrary(
  posts: { title: string; tree: any }[]
): BarebonesSectionLibrary {
  const barebonesSectionLibrary: BarebonesSectionLibrary = {};

  posts.forEach(post => {
    // Assuming post.tree has a root with children and each child has data.type
    if (post.tree && post.tree.root && Array.isArray(post.tree.root.children)) {
      barebonesSectionLibrary[post.title] = post.tree.root.children.filter(
        (n: any) => n.data && n.data.type === SECTION_SLUG
      );
    } else {
      barebonesSectionLibrary[post.title] = [];
    }
  });

  return barebonesSectionLibrary;
}

export async function createPageInWP(page: PageDescription) {
  const aiSettings: PageAISettings = {
    pageDescription: page,
    shouldGenerateOnInitialLoad: true
  };

  const aiSettingsAsJsonString = jsonStringifyOrUndefined(aiSettings);

  return pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_insert_post",
      friendlyName: "Insert Post",
      dataToPost: {
        postTitle: page.title,
        postType: "page",
        aiSettings: aiSettingsAsJsonString ? aiSettingsAsJsonString : false
      },
      decoder: t.type({
        postId: t.number
      })
    }),
    fold(
      () => null,
      data => {
        return {
          postId: data.postId,
          postTitle: page.title
        };
      }
    )
  );
}

async function setShouldGenerateOnInitialLoadToFalse() {
  const documentMeta = defensiveCopy(documentStore.document.documentMeta);

  lodash.set(documentMeta, "aiSettings.shouldGenerateOnInitialLoad", false);

  breakdanceUndo.ignore(async () => {
    documentStore.setDocumentMeta(documentMeta);
    await saveDocumentMetaAISettings(
      documentStore.document.documentMeta.id,
      documentStore.document.documentMeta.aiSettings
    );
  });
}

export async function populateDocumentFromPageDescription(
  pageDescription: PageDescription
): Promise<unknown> {
  for (const sectionDescription of pageDescription.sections) {
    const sectionToInsert = getSectionToInsert(sectionDescription);
    if (!sectionToInsert) continue;
    pasteElements([sectionToInsert], "inserting");
  }

  return await saveToWordPressWithErrorsLoggedToUiStore();
}

export async function shuffleSection(sectionId: TreeNode["id"]) {
  const maybeNode = findNode(documentStore.document.tree, sectionId);
  if (!maybeNode) return;

  const promptInfo = getPromptInfoFromNode(maybeNode);

  const sectionDescription: SectionDescription = {
    type:
      typeof promptInfo.onboarding.sectionType === "string"
        ? promptInfo.onboarding.sectionType
        : "Section",
    description: promptInfo.sectionDescription || ""
  };

  const sectionToInsert = getSectionToInsert(sectionDescription);
  if (!sectionToInsert) return;

  pasteElements([sectionToInsert], "inserting", {}, {
    mode: "replace",
    targetElementId: sectionId
  });
}

export async function maybePopulateCurrentDocumentFromPageDescription() {
  if (!documentStore.document.documentMeta.aiSettings) return;

  if (
    !documentStore.document.documentMeta.aiSettings.shouldGenerateOnInitialLoad
  )
    return;

  if (!documentStore.document.documentMeta.aiSettings.pageDescription) return;

  uiStore.setInterfaceState("loading");

  setShouldGenerateOnInitialLoadToFalse();

  await Promise.all([
    fetchAiSettings(),
    populateDocumentFromPageDescription(
      documentStore.document.documentMeta.aiSettings.pageDescription
    )
  ]);

  uiStore.setInterfaceState("normal");
}

function getRandomSectionFromBarebonesLibrary(sectionType: string): TreeNode | null {
  const sectionLibrary = getBarebonesSectionLibrary();

  const availableSections = sectionLibrary[sectionType];

  if (!availableSections || availableSections.length === 0) {
    console.log(
      `No sections available for type: ${sectionType} in library`
    );
    return null;
  }

  const randomIndex = Math.floor(Math.random() * availableSections.length);
  return availableSections[randomIndex];
}

function getSectionToInsert(sectionDescription: SectionDescription): TreeNode | null {
  const randomSection = getRandomSectionFromBarebonesLibrary(
    sectionDescription.type
  );

  if (!randomSection) return null;

  const sectionWithPrompt = addPromptInfoToSection(
    randomSection,
    sectionDescription
  );

  return sectionWithPrompt;
}

function addPromptInfoToSection(
  sectionNode: TreeNode,
  sectionDescription: SectionDescription
) {
  const promptInfo: PromptInfo = {
    sectionDescription: sectionDescription.description,
    qualifiers: "",
    temperature: 1,
    lockedFields: [],
    locale: "en_US",
    unsplash: {
      enabled: false,
      fillEmptyImagesOnly: false,
      keywords: ""
    },
    onboarding: {
      sectionType: sectionDescription.type,
      shouldGenerateOnInitialLoad: true
    },
    singularity: {
      shouldGenerateOnInitialLoad: true,
      sectionData: {
        type: sectionDescription.type,
        index: false
      },
      unsplashKeywords: []
    }
  
  };

  // do i defensive copy this?

  sectionNode.data.properties = {
    ...(sectionNode.data.properties || {}),
    meta: {
      ...(sectionNode.data.properties?.meta || {}),
      promptInfo: promptInfo
    }
  };

  return sectionNode;
}

export async function saveDocumentMetaAISettings(
  postId: number,
  aiSettings: DocumentMeta["aiSettings"]
) {
  return await makeWordPressAjaxRequest({
    friendlyName: "Saving Document AI Settings",
    action: "breakdance_save_document_ai_settings",
    dataToPost: {
      postId,
      settings: aiSettings ? fpJsonStringify(aiSettings) : false
    },
    decoder: t.unknown
  });
}

export function addAnotherSection(element: TreeNode) {
  const promptInfo = getPromptInfoFromNode(element);

  if (!promptInfo.onboarding.sectionType) return;

  const sectionToInsert = getSectionToInsert({
    type: promptInfo.onboarding.sectionType,
    description: promptInfo.sectionDescription
  });

  if (!sectionToInsert) return;

  uiStore.activateElement(element.id);
  pasteElements([sectionToInsert], "inserting");
}
